import { useState, useEffect, useRef } from 'react';
import { onNavClick } from './router.js';

const STORAGE_KEY = 'rm-auth-v2';

// Wraps gated routes. POSTs the passphrase to /api/unlock; on success,
// stores a session flag so further navigation inside the tab stays unlocked.
export default function AuthGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(STORAGE_KEY) === 'ok');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!unlocked && inputRef.current) inputRef.current.focus();
  }, [unlocked]);

  if (unlocked) return children;

  const onSubmit = async (e) => {
    e.preventDefault();
    const pw = inputRef.current.value;
    if (!pw) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/unlock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      if (res.ok) {
        sessionStorage.setItem(STORAGE_KEY, 'ok');
        setUnlocked(true);
        window.dispatchEvent(new CustomEvent('rm:unlocked'));
      } else if (res.status === 401) {
        setError('Passphrase not recognized.');
        setShake(true);
        setTimeout(() => setShake(false), 500);
        inputRef.current.value = '';
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="rm-auth">
      <div className="rm-auth__bg" />
      <div className={"rm-auth__card" + (shake ? ' is-shake' : '')}>
        <div className="rm-auth__head">
          <span className="rm-auth__dot" />
          <span>Ryan Marfone · Protected case study</span>
        </div>
        <h1 className="rm-auth__title">A <i>private</i> view.</h1>
        <p className="rm-auth__body">
          This case study contains client-confidential work. Enter the passphrase you were given.
        </p>
        <form className="rm-auth__form" onSubmit={onSubmit} autoComplete="off">
          <label className="rm-auth__label">Passphrase</label>
          <div className="rm-auth__row">
            <input ref={inputRef} type="password" spellCheck="false" data-cursor="link" disabled={submitting} />
            <button type="submit" data-cursor="link" disabled={submitting}>
              {submitting ? 'Checking…' : 'Unlock →'}
            </button>
          </div>
          <p className="rm-auth__error">{error}</p>
        </form>
        <div className="rm-auth__foot">
          <a href="/" onClick={onNavClick} data-cursor="link">← Back to index</a>
          <span>Session only</span>
        </div>
      </div>
    </div>
  );
}
